import React from 'react';
import { Linkedin, Twitter, Instagram, Facebook, Music2 } from 'lucide-react';
import GeneratedBrandIcon from './GeneratedBrandIcon';
import BrandIcon from './BrandIcon';
import {
  // TikTokGlyph,
  BoomplayGlyph,
  DeezerGlyph,
  TidalGlyph,
  AudiomackGlyph,
  SpotifyGlyph,
  YouTubeGlyph,
  SoundCloudGlyph,
} from './BrandGlyphs';

type UnifiedBrandIconProps = {
  platform: string;
  kind: 'social' | 'streaming';
  size?: number;
  className?: string;
  title?: string;
  color?: string; // accent color for generated badge
  useUploadedImages?: boolean; // if true, try BrandIcon first
};

const normalize = (s: string) => String(s || '').trim().toLowerCase();

export default function UnifiedBrandIcon({ platform, kind, size = 20, className = '', title, color = '#f59e0b', useUploadedImages = false }: UnifiedBrandIconProps) {
  // Normalize platform aliases (e.g., X -> twitter, domain-like inputs -> canonical key)
  const aliasKey = (raw: string) => {
    const k = normalize(raw);
    if (!k) return k;
    if (k === 'x' || k.includes('x.com')) return 'twitter';
    if (k.includes('twitter.com')) return 'twitter';
    if (k.includes('linkedin.com')) return 'linkedin';
    if (k.includes('instagram.com')) return 'instagram';
    if (k.includes('facebook.com')) return 'facebook';
    if (k.includes('tiktok.com')) return 'tiktok';
    if (k.includes('youtube.com') || k.includes('music.youtube.com') || k.includes('youtu.be')) return 'youtube';
    if (k.includes('soundcloud.com')) return 'soundcloud';
    if (k.includes('spotify.com')) return 'spotify';
    if (k.includes('deezer.com')) return 'deezer';
    if (k.includes('tidal.com')) return 'tidal';
    if (k.includes('audiomack.com')) return 'audiomack';
    if (k.includes('boomplay.com')) return 'boomplay';
    return k;
  };
  const key = aliasKey(platform);
  // Guard against too-light colors (low contrast on white)
  const parseHex = (hex: string): [number, number, number] | null => {
    const h = (hex || '').trim();
    const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(h);
    if (!m) return null;
    const v = m[1];
    if (v.length === 3) {
      const r = parseInt(v[0] + v[0], 16);
      const g = parseInt(v[1] + v[1], 16);
      const b = parseInt(v[2] + v[2], 16);
      return [r, g, b];
    }
    const r = parseInt(v.slice(0, 2), 16);
    const g = parseInt(v.slice(2, 4), 16);
    const b = parseInt(v.slice(4, 6), 16);
    return [r, g, b];
  };
  const relLum = (r: number, g: number, b: number) => {
    const toLin = (c: number) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const R = toLin(r), G = toLin(g), B = toLin(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };
  const rgb = parseHex(color);
  const isTooLight = rgb ? relLum(rgb[0], rgb[1], rgb[2]) > 0.8 : false;
  const effectiveColor = isTooLight ? '#111827' : color; // slate-900 fallback for contrast

  // 1) Known lucide socials
  const Social = key === 'linkedin' ? Linkedin
              : key === 'twitter' ? Twitter
              : key === 'instagram' ? Instagram
              : key === 'facebook' ? Facebook
              : key === 'tiktok' ? Music2
              : null;

  // 2) Curated glyphs for TikTok + streaming platforms (single-color, theme-tinted)
  const Glyph = key === 'boomplay' ? BoomplayGlyph
              : key === 'deezer' ? DeezerGlyph
              : key === 'tidal' ? TidalGlyph
              : key === 'audiomack' ? AudiomackGlyph
              : key === 'spotify' ? SpotifyGlyph
              : key === 'youtube' || key === 'youtube-music' ? YouTubeGlyph
              : key === 'soundcloud' ? SoundCloudGlyph
              : null;

  // Uploaded image option takes precedence but falls back to glyph/social/generated
  if (useUploadedImages) {
    const fallback = Social
      ? <Social width={size} height={size} />
      : Glyph
        ? <Glyph size={size} title={title} />
        : <GeneratedBrandIcon platform={platform} kind={kind} size={size} title={title} accentColor={color} />;
    return (
      <span className={className} style={{ color: effectiveColor }}>
        <BrandIcon platform={platform} kind={kind} size={size} title={title} fallback={fallback} />
      </span>
    );
  }

  // Prefer a consistent generated icon to preserve UI branding for all platforms
  if (Social && kind === 'social') {
    // Use platform’s lucide icon for well-known socials to match current UI
    return (
      <span className={className} style={{ color: effectiveColor }}>
        <Social width={size} height={size} />
      </span>
    );
  }

  // Prefer curated glyphs for TikTok/streaming; they inherit currentColor to match theme
  if (Glyph) {
    return (
      <span className={className} style={{ color: effectiveColor }}>
        <Glyph size={size} title={title} />
      </span>
    );
  }

  // Fallback to generated monogram
  return (
  <span className={className} style={{ color: effectiveColor }}>
      <GeneratedBrandIcon platform={platform} kind={kind} size={size} title={title} accentColor={color} />
    </span>
  );
}
